import React, { useState, useEffect, useCallback } from "react";
import {
    collection,
    query,
    orderBy,
    getDocs,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import DashboardWrapper from "../../components/layout/DashboardWrapper";
import {
    MagnifyingGlassIcon,
    XMarkIcon,
    BuildingOffice2Icon,
    ArrowPathIcon,
    ChevronRightIcon,
    PaperClipIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";

type RequestStatus = "new" | "in_review" | "approved" | "rejected";

interface CommercialRequest {
    id: string;
    owner: {
        name: string;
        email: string;
        jobTitle: string;
        organisation: string;
    };
    athleteCount: number;
    coachCount: number;
    medicalCount: number;
    managementCount: number;
    organisation: {
        type: string;
        size: string;
        website: string;
        addressLine1: string;
        addressLine2: string;
        city: string;
        postcode: string;
        country: string;
    };
    billing: {
        contact: string;
        email: string;
        address: string;
        abn: string;
    };
    teams: Record<string, string[]>;
    coaches: string[];
    athletes: string[];
    athletesCsv?: { url: string; fileName: string };
    submittedAt: Timestamp | null;
    status: RequestStatus;
}

const STATUS_CONFIG: Record<RequestStatus, { label: string; classes: string }> = {
    new:       { label: "New",       classes: "bg-blue-100 text-blue-700" },
    in_review: { label: "In Review", classes: "bg-yellow-100 text-yellow-700" },
    approved:  { label: "Approved",  classes: "bg-green-100 text-green-700" },
    rejected:  { label: "Rejected",  classes: "bg-red-100 text-red-700" },
};

const ALL_STATUSES: RequestStatus[] = ["new", "in_review", "approved", "rejected"];

const formatDate = (ts: Timestamp | null) => {
    if (!ts) return "—";
    return ts.toDate().toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const CommercialRequestsPage: React.FC = () => {
    const [requests, setRequests] = useState<CommercialRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<RequestStatus | "all">("all");
    const [selected, setSelected] = useState<CommercialRequest | null>(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, "commercial", "onboarding", "requests"),
                orderBy("submittedAt", "desc"),
            );
            const snap = await getDocs(q);
            const docs = snap.docs.map((d) => ({
                id: d.id,
                status: "new" as RequestStatus,
                ...d.data(),
            })) as CommercialRequest[];
            setRequests(docs);
        } catch (err) {
            console.error("[CommercialRequests] Failed to fetch:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleStatusChange = async (req: CommercialRequest, newStatus: RequestStatus) => {
        setUpdatingStatus(true);
        try {
            await updateDoc(doc(db, "commercial", "onboarding", "requests", req.id), {
                status: newStatus,
            });
            const updated = { ...req, status: newStatus };
            setRequests((prev) => prev.map((r) => (r.id === req.id ? updated : r)));
            setSelected(updated);
        } catch (err) {
            console.error("[CommercialRequests] Failed to update status:", err);
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        setDeleting(true);
        try {
            await deleteDoc(doc(db, "commercial", "onboarding", "requests", selected.id));
            setRequests((prev) => prev.filter((r) => r.id !== selected.id));
            setSelected(null);
            setDeleteConfirmOpen(false);
        } catch (err) {
            console.error("[CommercialRequests] Failed to delete:", err);
        } finally {
            setDeleting(false);
        }
    };

    const filtered = requests.filter((r) => {
        const q = search.toLowerCase();
        const owner = r.owner ?? (r as any).contact;
        const matchesSearch =
            !q ||
            owner?.name?.toLowerCase().includes(q) ||
            owner?.email?.toLowerCase().includes(q) ||
            r.organisation?.type?.toLowerCase().includes(q);
        const matchesStatus = statusFilter === "all" || r.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    return (
        <DashboardWrapper>
            <div className="p-6 flex flex-col gap-6 min-h-[calc(100vh-64px)]">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Onboarding Requests</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {requests.length} submission{requests.length !== 1 ? "s" : ""} total
                        </p>
                    </div>
                    <button
                        onClick={fetchRequests}
                        className="btn btn-ghost btn-sm gap-2"
                        title="Refresh"
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                        Refresh
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by organisation, contact or email…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input input-bordered input-sm w-full pl-9"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {(["all", ...ALL_STATUSES] as const).map((s) => (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`btn btn-sm !shadow-none rounded-full border ${
                                    statusFilter === s
                                        ? "bg-gray-900 text-white border-gray-900"
                                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
                                }`}
                            >
                                {s === "all" ? "All" : STATUS_CONFIG[s].label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex gap-6 flex-1 min-h-0">
                    {/* Table */}
                    <div className={`flex-1 min-w-0 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col transition-all ${selected ? "hidden lg:flex" : "flex"}`}>
                        {loading ? (
                            <div className="flex-1 flex items-center justify-center">
                                <span className="loading loading-spinner loading-md text-gray-400" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400">
                                <BuildingOffice2Icon className="h-10 w-10" />
                                <p className="text-sm">No requests found</p>
                            </div>
                        ) : (
                            <div className="overflow-auto">
                                <table className="table table-sm w-full">
                                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="font-medium py-3 px-4">Organisation</th>
                                            <th className="font-medium py-3 px-4">Contact</th>
                                            <th className="font-medium py-3 px-4 hidden md:table-cell">Type</th>
                                            <th className="font-medium py-3 px-4 hidden lg:table-cell">Size</th>
                                            <th className="font-medium py-3 px-4 hidden sm:table-cell">Submitted</th>
                                            <th className="font-medium py-3 px-4">Status</th>
                                            <th className="w-8" />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filtered.map((req) => {
                                            const owner = req.owner ?? (req as any).contact;
                                            return (<tr
                                                key={req.id}
                                                onClick={() => setSelected(req)}
                                                className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                                                    selected?.id === req.id ? "bg-gray-50" : ""
                                                }`}
                                            >
                                                <td className="py-3 px-4">
                                                    <div className="font-medium text-gray-900 text-sm">
                                                        {owner?.organisation ?? "—"}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        {req.organisation?.city}{req.organisation?.country ? `, ${req.organisation.country}` : ""}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="text-sm text-gray-700">{owner?.name ?? "—"}</div>
                                                    <div className="text-xs text-gray-400">{owner?.jobTitle}</div>
                                                </td>
                                                <td className="py-3 px-4 hidden md:table-cell text-sm text-gray-600">
                                                    {req.organisation?.type || "—"}
                                                </td>
                                                <td className="py-3 px-4 hidden lg:table-cell text-sm text-gray-600">
                                                    {req.organisation?.size || "—"}
                                                </td>
                                                <td className="py-3 px-4 hidden sm:table-cell text-sm text-gray-500">
                                                    {formatDate(req.submittedAt)}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[req.status ?? "new"].classes}`}>
                                                        {STATUS_CONFIG[req.status ?? "new"].label}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-2 text-gray-300">
                                                    <ChevronRightIcon className="h-4 w-4" />
                                                </td>
                                            </tr>);
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Detail panel */}
                    {selected && (() => {
                        const selectedOwner = selected.owner ?? (selected as any).contact;
                        return (
                        <div className="w-full lg:w-[420px] flex-shrink-0 bg-white rounded-xl border border-gray-200 overflow-y-auto flex flex-col">
                            {/* Panel header */}
                            <div className="flex items-start justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
                                <div>
                                    <h2 className="font-semibold text-gray-900 text-base leading-tight">
                                        {selectedOwner?.name ?? "—"}
                                    </h2>
                                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(selected.submittedAt)}</p>
                                </div>
                                <button
                                    onClick={() => setSelected(null)}
                                    className="btn btn-ghost btn-xs btn-square ml-2 flex-shrink-0"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="p-5 flex flex-col gap-6">
                                {/* Status selector */}
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Status</p>
                                    <div className="flex gap-2 flex-wrap">
                                        {ALL_STATUSES.map((s) => (
                                            <button
                                                key={s}
                                                disabled={updatingStatus}
                                                onClick={() => handleStatusChange(selected, s)}
                                                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                                                    selected.status === s
                                                        ? `${STATUS_CONFIG[s].classes} border-transparent`
                                                        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                                                }`}
                                            >
                                                {STATUS_CONFIG[s].label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Owner */}
                                <Section title="Owner">
                                    <Field label="Name" value={selectedOwner?.name} />
                                    <Field label="Email" value={selectedOwner?.email} />
                                    <Field label="Job Title" value={selectedOwner?.jobTitle} />
                                </Section>

                                {/* Organisation */}
                                <Section title="Organisation">
                                    <Field label="Type" value={selected.organisation.type} />
                                    <Field label="Size" value={selected.organisation.size} />
                                    {selected.organisation.website && (
                                        <Field label="Website" value={selected.organisation.website} />
                                    )}
                                    <Field
                                        label="Address"
                                        value={[
                                            selected.organisation.addressLine1,
                                            selected.organisation.addressLine2,
                                            selected.organisation.city,
                                            selected.organisation.postcode,
                                            selected.organisation.country,
                                        ].filter(Boolean).join(", ")}
                                    />
                                </Section>

                                {/* Billing */}
                                <Section title="Billing">
                                    <Field label="Contact" value={selected.billing.contact} />
                                    <Field label="Email" value={selected.billing.email} />
                                    <Field
                                        label="Address"
                                        value={[
                                            selected.billing.address,
                                        ].filter(Boolean).join(", ")}
                                    />
                                    {selected.billing.abn && (
                                        <Field label="ABN" value={selected.billing.abn} />
                                    )}
                                </Section>

                                {/* Teams */}
                                {selected.teams && Object.keys(selected.teams).length > 0 && (
                                    <Section title={`Teams (${Object.keys(selected.teams).length})`}>
                                        <ul className="flex flex-col gap-3">
                                            {Object.entries(selected.teams).map(([teamName, groups]) => (
                                                <li key={teamName}>
                                                    <p className="text-xs font-medium text-gray-700">{teamName}</p>
                                                    {Array.isArray(groups) && groups.length > 0 && (
                                                        <ul className="flex flex-wrap gap-1.5 mt-1">
                                                            {groups.map((group, i) => (
                                                                <li key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-500">
                                                                    {group}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </Section>
                                )}

                                {/* Coaches (legacy) */}
                                {(selected as any).coaches && (
                                    <Section title={`Coaches (${((selected as any).coaches ?? []).length})`}>
                                        {((selected as any).coaches ?? []).length > 0 ? (
                                            <ul className="flex flex-col gap-1">
                                                {((selected as any).coaches as string[]).map((c, i) => (
                                                    <li key={i} className="text-xs text-gray-700">
                                                        {c}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-xs text-gray-400">None listed</p>
                                        )}
                                    </Section>
                                )}

                                {/* Counts (new structure) */}
                                {selected.athleteCount !== undefined && (
                                    <Section title="Members">
                                        <Field label="Athletes" value={String(selected.athleteCount ?? 0)} />
                                        <Field label="Coaches" value={String(selected.coachCount ?? 0)} />
                                        <Field label="Medical" value={String(selected.medicalCount ?? 0)} />
                                        <Field label="Management" value={String(selected.managementCount ?? 0)} />
                                    </Section>
                                )}

                                {/* Athletes CSV (legacy) */}
                                {selected.athletesCsv && (
                                    <Section title="Athletes CSV">
                                        <a
                                            href={selected.athletesCsv.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-xs text-gray-700 w-fit"
                                        >
                                            <PaperClipIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                            {selected.athletesCsv.fileName}
                                        </a>
                                    </Section>
                                )}

                                {/* Athletes list (legacy) */}
                                {((selected as any).athletes ?? []).length > 0 && (
                                    <Section title={`Athletes (${((selected as any).athletes as string[]).length})`}>
                                        <ul className="flex flex-col gap-1">
                                            {((selected as any).athletes as string[]).map((a, i) => (
                                                <li key={i} className="text-xs text-gray-700">{a}</li>
                                            ))}
                                        </ul>
                                    </Section>
                                )}
                            </div>

                            {/* Delete button */}
                            <div className="p-5 border-t border-gray-100 mt-auto">
                                <button
                                    onClick={() => setDeleteConfirmOpen(true)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                    Delete Request
                                </button>
                            </div>
                        </div>
                    );})()}
                </div>
            </div>

            {/* Delete confirmation modal */}
            {deleteConfirmOpen && selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => !deleting && setDeleteConfirmOpen(false)}
                    />
                    <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <TrashIcon className="h-5 w-5 text-red-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 text-base">Delete Request</h3>
                                <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600">
                            Are you sure you want to delete the request from{" "}
                            <span className="font-medium text-gray-900">{(selected.owner ?? (selected as any).contact)?.name ?? "—"}</span>?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setDeleteConfirmOpen(false)}
                                disabled={deleting}
                                className="btn btn-ghost btn-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="btn btn-sm bg-red-600 hover:bg-red-700 text-white border-none"
                            >
                                {deleting ? <span className="loading loading-spinner loading-xs" /> : null}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardWrapper>
    );
};

// ── Small layout helpers ──────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{title}</p>
        <div className="flex flex-col gap-2">{children}</div>
    </div>
);

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex items-start gap-2">
        <div className="min-w-0">
            <p className="text-xs text-gray-400">{label}</p>
            <p className="text-sm text-gray-800 break-words">{value || "—"}</p>
        </div>
    </div>
);

export default CommercialRequestsPage;
